import React, { useState, useEffect, useContext, useMemo } from "react";
import OfficeSectionsHeader from "../../components/OfficeSectionsHeader/OfficeSectionsHeader";
import BreadCrumbs from "../../components/BreadCrumbs/BreadCrumbs";
import { Button, ConfirmationDialog, Pagination, TableHeader, TableRow, TextInput } from "../../components";
import { useNavigate } from "react-router-dom";
import FileUploadTemplate from "./FileUploadTemplate";
import { headersType, ToastVariants } from "../../models";
import { templateDeleteByTemplateName, getAllTemplateData, uploadCommonFile } from "../../redux/slices/EmailTemplateSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { PayloadAction } from "@reduxjs/toolkit";
import Delete from "../../assets/images/delete-file.svg";
import Edit from "../../assets/images/edit.svg";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import DataNotFound from "../../components/DataNotFound/DataNotFound";
import NotFound from "../../assets/images/no-forms.png";
import { sortObj } from "../../redux/slices/OfficeSlice";
import { useDebouncedCallback } from "use-debounce";
import { resetUploadState, setProgress, setUploading } from "../../redux/slices/fileUploadSlice";

interface UploadResponse {
  status: string;
  message: string;
  statusMessage?: string;
}

export const headersInitialState: headersType[] = [
  {
    title: "Template Name",
    extraClasses: "w-full pl-0 truncate",
    sortKey: "templateName",
    isClickable: false,
  },
  {
    title: "",
    extraClasses: "w-40 pr-0",
    sortKey: "",
    isClickable: false,
  },
];

interface Template {
  templateId: string;
  templateName: string;
  templateContent: string;
  deleted: boolean;
}

const EmailListing = ({ isAdminPage }: { isAdminPage: boolean }) => {
  const { templateList, sort, loading } = useSelector((state: RootState) => state.EmailTemplateSlice);
  const sampleFile = process.env.REACT_APP_EMAIL_TEMPLATE_SAMPLE_FILE;
  const navigate = useNavigate();
  const toast = useContext(ToastContext);
  const dispatch = useDispatch<AppDispatch>();
  const [headers] = useState(headersInitialState);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState("");
  const [templateData, setTemplateData] = useState<Template[]>([]);
  const SEARCH_DEBOUNCE_DELAY_MS = 1000;
  const debounced = useDebouncedCallback(value => {
    if (value && value.trim().length >= 2) {
      getTemplateData({ ...sort, pageNumber: 0 }, value.trim());
    }
  }, SEARCH_DEBOUNCE_DELAY_MS);

  const handleSearchInput = (text: string) => {
    const input = text;
    const oldSearchTerm = searchTerm;
    setSearchTerm(input);
    debounced(input);
    if (oldSearchTerm !== "" && input.length <= 1) {
      getTemplateData(sort);
    }
  };
  const handleCancel = (val?: boolean) => {
    setShowModal(false);
    dispatch(resetUploadState());
  };
  const getTemplateData = (sortObj?: sortObj, searchText?: string) => {
    const pageNumToBeCalled = sortObj?.pageNumber ?? sort.pageNumber;
    const sizeToBeCalled = sortObj?.rowsPerPage ?? sort.rowsPerPage;
    const sortBy = sortObj?.sortedBy ?? sort.sortedBy;
    const searchT = searchText?.trim() ?? "";
    dispatch(
      getAllTemplateData({
        page: pageNumToBeCalled,
        size: sizeToBeCalled,
        sortType: sortBy,
        searchTerm: searchT.length > 2 ? searchT : "",
      })
    ).then((value: PayloadAction<any>) => {
      setTemplateData(value?.payload?.data?.content);
    });
  };
  useEffect(() => {
    getTemplateData();
  }, []);

  const handleDelete = async () => {
    const res = await dispatch(templateDeleteByTemplateName({ formId: showDeleteModal }));
    const data = res.payload as UploadResponse;
    if (data?.status === "true") {
      toast?.openToast(data?.statusMessage, 2000, ToastVariants.Success);
      getTemplateData();
    } else {
      toast?.openToast(data?.statusMessage, 2000, ToastVariants.Error);
    }
    setShowDeleteModal("");
  };

  const extraClasses = useMemo(() => headers.map(head => head.extraClasses), [headers]);
  const handleEdit = (templateName: string) => navigate(`/add-edit-template?templateName=${templateName}`);
  const handleDeleteClick = (templateName: string) => setShowDeleteModal(templateName);

  const onPageChange = async (e: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    getTemplateData({ sortedBy: sort.sortedBy, pageNumber: newPage, rowsPerPage: sort.rowsPerPage });
  };
  const onRowsPerPageChange = async (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    getTemplateData({ sortedBy: sort.sortedBy, pageNumber: 0, rowsPerPage: parseInt(event.target.value, 10) });
  };

  const { file, uploading, progress } = useSelector((state: RootState) => state.fileUpload);

  const handleUploadFile = async () => {
    if (file) {
      try {
        dispatch(setUploading(true));
        const res = await dispatch(
          uploadCommonFile({
            file,
            callback: (progress: number) => dispatch(setProgress(progress)),
          })
        );
        const payload = res.payload as UploadResponse;
        const isSuccess = payload?.status?.toLowerCase() === "true";

        toast?.openToast(payload.statusMessage, 2000, isSuccess ? ToastVariants.Success : ToastVariants.Error);
        handleCancel();
        if (isSuccess) getTemplateData();
      } catch (_) {
        toast?.openToast("Something went wrong!", 2000, ToastVariants.Error);
      } finally {
        dispatch(setUploading(false));
      }
    }
  };

  return (
    <main>
      <div className="container mx-auto p-8">
        {!isAdminPage ? <OfficeSectionsHeader showMeta={true} /> : <BreadCrumbs items={[{ key: "#", title: "Templates" }]} />}
        <div className="block lg:flex justify-between items-center mb-4">
          <div className="mb-5 lg:mb-0 w-[-webkit-fill-available]">
            <h2 className="heading uppercase">Email Templates</h2>
          </div>
          <div className="block md:flex lg:justify-end flex-wrap w-full gap-2 items-center">
            <div className="flex items-center">
              <div className="relative w-full">
                <TextInput
                  onChangeFunc={e => {
                    handleSearchInput(e.currentTarget.value);
                  }}
                  value={searchTerm}
                  placeholder="Search Templates"
                  extraInputClass="w-full"
                  isSearchInput={true}
                />
              </div>
            </div>
            <Button className="w-auto" text="Create New Template" onClick={() => navigate("/add-edit-template")} />
          </div>
        </div>
        <div className="w-fit mb-2">
          <Button text="Upload Template" onClick={() => setShowModal(true)} className="whitespace-nowrap w-auto mt-3 md:mt-0" />
        </div>
        <div className="flex justify-between items-center border-b border-docnote-blue-light border-opacity-50">
          {headers.map((head, index: number) => (
            <TableHeader
              title={head.title}
              index={index}
              key={index}
              sortIcon={null}
              isClickable={head.isClickable}
              handleSort={() => null}
              extraClassesHeader={extraClasses[index]}
              isCheckbox={head?.isCheckbox}
              selectAll={() => null}
            />
          ))}
        </div>
        {templateData.length ? (
          <>
            {templateList?.content.map((rowData, index) => {
              const sortedData = {
                name: rowData.templateName,
                status: (
                  <div className="flex gap-3 justify-end">
                    <>
                      <img src={Edit} alt="edit icon" onClick={() => handleEdit(rowData.templateName)} />
                      <img src={Delete} alt="delete icon" onClick={() => handleDeleteClick(rowData.templateName)} />
                    </>
                  </div>
                ),
              };
              return (
                <div
                  // className={rowData?.deleted ? "hidden" : "block"}
                  key={index}
                >
                  <TableRow
                    data={Object.values(sortedData)}
                    key={index}
                    id={index.toString()}
                    extraClassesRow={extraClasses}
                    handleCloseTooltip={() => null}
                    handleOpenTooltip={() => null}
                    tooltipVisible={false}
                    onRowClick={() => null}
                  />
                </div>
              );
            })}
          </>
        ) : (
          <>
            <DataNotFound image={NotFound} text="No Data found" />
          </>
        )}
        <Pagination
          colSpan={10}
          count={templateList?.content ? templateList.totalElements : 0}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          page={sort.pageNumber}
          rowsPerPage={sort.rowsPerPage}
        />
      </div>
      <ConfirmationDialog
        open={showModal}
        description={
          <FileUploadTemplate handleCancel={handleCancel} showDemoFile={true} getTemplateData={() => handleUploadFile()} sampleFile={sampleFile} />
        }
        successButtonText={""}
        failureButtonText={""}
        handleSuccess={() => null}
        handleFailure={() => null}
      />
      <ConfirmationDialog
        open={showDeleteModal?.length > 0}
        description={"Are you sure you want to delete this Template? This action cannot be undone."}
        successButtonText={"Yes"}
        failureButtonText={"No"}
        handleSuccess={handleDelete}
        handleFailure={() => setShowDeleteModal("")}
      />
    </main>
  );
};
export default EmailListing;
